import { GridOptions, ColDef } from 'ag-grid-community';

// AG Grid performance configuration
export const createFanGainGridOptions = (
  onCellValueChanged: (params: any) => void
): GridOptions => ({
  // Core performance settings
  animateRows: false,
  suppressColumnVirtualisation: false,
  suppressRowVirtualisation: false,
  rowBuffer: 10,

  // Change detection optimization
  getRowId: (params) => params.data?.id || '',

  // Rendering optimizations
  suppressCellFocus: false,
  ensureDomOrder: true,
  suppressScrollOnNewData: true,
  suppressLoadingOverlay: true,
  suppressNoRowsOverlay: true,

  // Update optimizations
  asyncTransactionWaitMillis: 50,
  suppressAggFuncInHeader: true,

  // Memory optimization
  maxConcurrentDatasourceRequests: 2,
  cacheBlockSize: 100,
  maxBlocksInCache: 10,
  purgeClosedRowNodes: true,

  // Viewport optimization
  viewportRowModelPageSize: 100,
  viewportRowModelBufferSize: 20,

  // Cell selection and editing
  cellSelection: true,
  suppressCopyRowsToClipboard: false,
  suppressCopySingleCellRanges: false,

  // Event handlers
  onCellValueChanged,

  // Row styling
  rowClassRules: {
    'fan-gain-totals-row': (params) => params.data?.is_total_row,
    'fan-gain-user-header': (params) => params.data?.is_user_header,
    'fan-gain-model-row': (params) => params.data?.row_type === 'model'
  },

  // Default column configuration
  defaultColDef: {
    resizable: false,
    sortable: false,
    filter: false,
    menuTabs: [],
    suppressMovable: true,
    suppressHeaderFilterButton: true,
    suppressHeaderMenuButton: true,
    cellClass: 'ag-cell-no-focus'
  },

  // Grid layout
  domLayout: 'normal',
  headerHeight: 42,
  rowHeight: 25,

  // Performance monitoring (development only)
  ...(process.env.NODE_ENV === 'development' && {
    debug: false,
    suppressConsoleAssert: true
  })
});

// Column definition factory
export const createModelColumnDef = (): ColDef => ({
  headerName: '',
  field: 'model',
  pinned: 'left',
  width: 150,
  resizable: false,
  sortable: false,
  filter: false,
  menuTabs: [],
  suppressMovable: true,
  cellStyle: {
    textAlign: 'left',
    fontFamily: 'Arial, sans-serif',
    fontWeight: 700,
    fontSize: '15px',
    paddingLeft: 8
  }
});

// Day column definition factory
export const createDayColumnDef = (day: number): ColDef => ({
  headerName: String(day),
  field: `day${day}`,
  width: 60,
  type: 'numericColumn',
  cellClass: 'text-center ag-cell-no-focus',
  headerClass: 'day-header',
  resizable: false,
  sortable: false,
  filter: false,
  menuTabs: [],
  suppressMovable: true,
  editable: (params: any) => params.data?.row_type === 'model',
  cellEditor: 'agNumberCellEditor',
  cellEditorParams: {
    min: 0,
    precision: 0,
    step: 1
  },
  valueParser: (params: any) => {
    const value = params.newValue;
    if (value === '' || value == null) return 0;
    const parsed = Number(value);
    return isNaN(parsed) ? 0 : Math.max(0, Math.floor(parsed));
  },
  cellStyle: (params: any) => {
    const isZeroValue = params.value === 0 || params.value === '';
    const isUserHeader = params.data?.is_user_header;
    const isTotalRow = params.data?.is_total_row;

    const style: any = {
      textAlign: 'right',
      fontFamily: 'Arial, sans-serif',
      fontWeight: 700,
      fontSize: '15px',
      paddingRight: 4
    };

    if (isTotalRow) {
      style.backgroundColor = '#f0f9ff';
      style.borderTop = '2px solid #0ea5e9';
      style.color = '#0ea5e9';
    } else if (isUserHeader) {
      style.backgroundColor = '#f9fafb';
      style.borderTop = '2px solid #d1d5db';
      style.color = '#6b7280';
    } else if (isZeroValue) {
      style.color = '#9ca3af';
    }

    return style;
  },
  valueFormatter: (params: any) => {
    if (params.data?.is_user_header) {
      return '';
    }
    if (typeof params.value === 'number' && params.value > 0) {
      return params.value.toLocaleString();
    }
    return '';
  }
});

// CSS classes for grid styling
export const gridCssClasses = `
  .fan-gain-totals-row {
    background-color: #f0f9ff !important;
    border-top: 2px solid #0ea5e9 !important;
    font-weight: bold !important;
  }

  .fan-gain-user-header {
    background-color: #f9fafb !important;
    border-top: 2px solid #d1d5db !important;
    font-weight: bold !important;
  }

  .fan-gain-model-row:hover {
    background-color: #f8fafc !important;
  }

  .ag-cell-data-changed {
    background-color: #dbeafe !important;
    transition: background-color 0.3s ease-out;
  }

  .day-header {
    font-weight: bold !important;
    text-align: center !important;
    border-bottom: 2px solid #e5e7eb !important;
  }

  .ag-cell-no-focus:focus {
    outline: none !important;
    border: 1px solid #3b82f6 !important;
  }

  .ag-cell-edit-input {
    text-align: right !important;
    font-family: Arial, sans-serif !important;
    font-weight: bold !important;
    font-size: 15px !important;
  }
`;